import React, { useEffect, useRef } from "react";

const EvaluationVideo = ({
  url,
  frames = {},
  predictionInterval,
  video_fps,
  playMode,
  onFrameChange,
  labelvisible,
  opacity,
  currentColors = {},
  showPredictions = true,
  landmarks,
}) => {
  const Objectframes = Array.isArray(frames)
    ? frames.reduce((obj, item, i) => ((obj[i] = item), obj), {})
    : frames;

  const ObjectLandmarks = Array.isArray(landmarks)
    ? landmarks.reduce((obj, item, i) => ((obj[i] = item), obj), {})
    : (landmarks || {});

  const framesRef = useRef(Objectframes);
  const landmarksRef = useRef(ObjectLandmarks);

  useEffect(() => {
    framesRef.current = Array.isArray(frames)
      ? frames.reduce((obj, item, i) => ((obj[i] = item), obj), {})
      : (frames || {});
    if (videoRef.current?.paused) drawFrame();
  }, [frames]);

  useEffect(() => {
    landmarksRef.current = Array.isArray(landmarks)
      ? landmarks.reduce((obj, item, i) => ((obj[i] = item), obj), {})
      : (landmarks || {});
    if (videoRef.current?.paused) drawFrame();
  }, [landmarks]);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const videoFrameCallbackRef = useRef(null);
  const currentPredictionFrameRef = useRef(-1);
  const lastFrameNotificationRef = useRef(0);
  const isDrawingRef = useRef(false);

  const labelVisibleRef = useRef(labelvisible);
  const opacityRef = useRef(opacity);
  const currentColorsRef = useRef(currentColors);
  const showPredictionsRef = useRef(showPredictions);
 

  useEffect(() => {
    showPredictionsRef.current = showPredictions;
    if (videoRef.current?.paused) {
      drawFrame();
    }
  }, [showPredictions]);

  useEffect(() => {
    labelVisibleRef.current = labelvisible;
    if (videoRef.current?.paused) {
      drawFrame();
    }
  }, [labelvisible]);


  useEffect(() => {
    opacityRef.current = opacity;
    if (videoRef.current?.paused) drawFrame();
  }, [opacity]);

  useEffect(() => {
    currentColorsRef.current = currentColors;
    if (videoRef.current?.paused) drawFrame();
  }, [currentColors]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleSeek = () => {
      if (video.paused) {
        drawFrame();
      }
    };

    video.addEventListener("seeked", handleSeek);
    return () => {
      video.removeEventListener("seeked", handleSeek);
    };
  }, []);

  // --- draw one frame ---
  const drawFrame = (mediaTime) => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    const ctx = canvas.getContext("2d");
    const displayWidth = video.clientWidth;
    const displayHeight = video.clientHeight;

    const canvasWidth = Math.max(1, Math.round(displayWidth));
    const canvasHeight = Math.max(1, Math.round(displayHeight));
    if (canvas.width !== canvasWidth || canvas.height !== canvasHeight) {
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
    }
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const fps = Number(video_fps);
    const currentFrame = Math.floor(
      (Number.isFinite(mediaTime) ? mediaTime : video.currentTime) *
        (Number.isFinite(fps) && fps > 0 ? fps : 30)
    );

    const intervalValue = Number(predictionInterval);
    const interval =
      Number.isFinite(intervalValue) && intervalValue > 0
        ? Math.floor(intervalValue)
        : 1;
    const predictionFrame = Math.floor(currentFrame / interval) * interval;

    // The parent only uses this value to select prediction data. Updating its
    // state for every decoded video frame rerenders the entire modal at video
    // frame rate and creates severe allocation/GC pressure. Notify it only
    // when the prediction window actually changes.
    const notificationTime = performance.now();
    if (
      currentPredictionFrameRef.current !== predictionFrame &&
      typeof onFrameChange === "function" &&
      (video.paused || notificationTime - lastFrameNotificationRef.current >= 200)
    ) {
      currentPredictionFrameRef.current = predictionFrame;
      lastFrameNotificationRef.current = notificationTime;
      onFrameChange(predictionFrame);
    }

    const boxes = showPredictionsRef.current
      ? framesRef.current?.[currentFrame] ||
        framesRef.current?.[predictionFrame] ||
        []
      : [];

    if (!video.videoWidth || !video.videoHeight) return;

    const scale = Math.max(
      displayWidth / video.videoWidth,
      displayHeight / video.videoHeight
    );
    const offsetX = (displayWidth - video.videoWidth * scale) / 2;
    const offsetY = (displayHeight - video.videoHeight * scale) / 2;

    boxes.forEach((box) => {
      const x = box.x * scale + offsetX;
      const y = box.y * scale + offsetY;
      const w = box.w * scale;
      const h = box.h * scale;

      // remove score suffix "(0.97)" if present

      // console.log("Box from parent:", {
      //   original: { x: box.x, y: box.y, w: box.w, h: box.h },
      //   scaled: { x, y, w, h },
      //   label: box.label,
      //   color: currentColorsRef.current[box.label] || box.color,
      // });


      const pureLabel =
        typeof box.label === "string"
          ? box.label.replace(/\s*\([\d.]+\)$/, "")
          : box.label;

      // ✅ always latest color
      const color = currentColorsRef.current[pureLabel] || box.color;

      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.globalAlpha = 1;
      ctx.strokeRect(x, y, w, h);

      if (playMode === "controls" && opacityRef.current > 0) {
        ctx.fillStyle = color;
        ctx.globalAlpha = opacityRef.current;
        ctx.fillRect(x, y, w, h);
      }

      if (box.label && labelVisibleRef.current) {
        ctx.globalAlpha = 1;
        ctx.font = "bold 12px Arial";
        ctx.fillStyle = playMode === "controls" ? "#fff" : color;

        const text = box.label;
        const paddingX = 4;
        const paddingY = 2;
        const metrics = ctx.measureText(text);
        const textWidth = metrics.width;
        const textHeight = 14;

        if (playMode === "controls") {
          ctx.fillStyle = color;
          ctx.fillRect(
            x,
            y - textHeight - paddingY,
            textWidth + paddingX * 2,
            textHeight + paddingY * 2
          );
          ctx.fillStyle = "#fff";
        }

        ctx.fillText(text, x + paddingX, y - 4);
      }
    });

    // --- LANDMARKS ---
    const lmForFrame = showPredictionsRef.current
      ? (landmarksRef.current?.[currentFrame] ||
        landmarksRef.current?.[predictionFrame] || [])
      : [];

    // Normalize shape to "faces[]", where each face is 5 points.
    const facesLandmarks = (() => {
      if (!lmForFrame) return [];
      // Case: single face => [[x,y]...]
      if (Array.isArray(lmForFrame) && lmForFrame.length && typeof lmForFrame[0]?.[0] === "number") {
        return [lmForFrame];
      }
      // Case: multi face => [[[x,y]...], ...]
      return Array.isArray(lmForFrame) ? lmForFrame : [];
    })();

    const drawPoint = (ctx, px, py, r = 3, fill = "#00ff00", stroke = "#000") => {
      ctx.save();
      ctx.globalAlpha = 1;
      ctx.beginPath();
      ctx.arc(px, py, r, 0, Math.PI * 2);
      ctx.fillStyle = fill;
      ctx.fill();
      ctx.lineWidth = 1;
      ctx.strokeStyle = stroke;
      ctx.stroke();
      ctx.restore();
    };

    facesLandmarks.forEach((facePts, faceIdx) => {
      // pick a color (optional): tie to face/box label if you want
      const color = "#00ff00";

      // Optionally draw a tiny index label per face
      // ctx.fillStyle = color; ctx.fillText(`F${faceIdx}`, ...);

      facePts.forEach(([lx, ly], ptIdx) => {
        const px = lx * scale + offsetX;
        const py = ly * scale + offsetY;
        drawPoint(ctx, px, py, 3, color, "#000");
      });

      // Optional: connect points in a simple way (eyes-nose-mouth corners)
      // left eye(0) -> right eye(1) -> nose(2) -> left mouth(3) -> right mouth(4)
     
    });
  };

  // --- continuous drawing loop while playing ---
  const startDrawing = () => {
    const video = videoRef.current;
    if (!video || animationRef.current || videoFrameCallbackRef.current) return;
    isDrawingRef.current = true;

    if (typeof video.requestVideoFrameCallback === "function") {
      const drawVideoFrame = (_now, metadata) => {
        if (!isDrawingRef.current || video.paused || video.ended) {
          videoFrameCallbackRef.current = null;
          return;
        }

        drawFrame(metadata?.mediaTime);
        if (isDrawingRef.current && !video.paused && !video.ended) {
          videoFrameCallbackRef.current =
            video.requestVideoFrameCallback(drawVideoFrame);
        } else {
          videoFrameCallbackRef.current = null;
        }
      };

      videoFrameCallbackRef.current =
        video.requestVideoFrameCallback(drawVideoFrame);
      return;
    }

    const draw = () => {
      if (video.paused || video.ended) {
        stopDrawing();
        return;
      }
      drawFrame();
      animationRef.current = requestAnimationFrame(draw);
    };
    animationRef.current = requestAnimationFrame(draw);
  };

  const stopDrawing = () => {
    const video = videoRef.current;
    isDrawingRef.current = false;
    if (
      videoFrameCallbackRef.current !== null &&
      typeof video?.cancelVideoFrameCallback === "function"
    ) {
      video.cancelVideoFrameCallback(videoFrameCallbackRef.current);
      videoFrameCallbackRef.current = null;
    }

    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
  };

  useEffect(() => {
    currentPredictionFrameRef.current = -1;
    lastFrameNotificationRef.current = 0;
  }, [url]);

  useEffect(() => () => {
    stopDrawing();

    const video = videoRef.current;
    if (video) {
      video.pause();
      video.removeAttribute("src");
      video.load();
    }

    const canvas = canvasRef.current;
    if (canvas) {
      // Explicitly release the canvas backing store (which may live in GPU
      // memory) when a preview/player is removed.
      canvas.width = 0;
      canvas.height = 0;
    }
  }, []);

  return (
    <div
      style={{
        height: playMode === "hover" ? "100%" : "calc(100vh - 450px)",
        position: "relative",
      }}
    >
      <video
        ref={videoRef}
        src={url || undefined}
        className="custom-video-controls"
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          objectPosition: "center",
          display: "block",
        }}
        muted={playMode === "hover"}
        loop
        playsInline
        disablePictureInPicture
        preload="metadata"
        autoPlay={playMode === "controls"}
        controls={playMode === "controls"}
        onLoadedData={drawFrame}
        {...(playMode === "hover"
          ? {
            onMouseEnter: (e) => {
              e.currentTarget.currentTime = 0;
              e.currentTarget.play().catch(() => { });
              startDrawing();
            },
            onMouseLeave: (e) => {
              e.currentTarget.pause();
              e.currentTarget.currentTime = 0;
              stopDrawing();
            },
          }
          : {
            onPlay: startDrawing,
            onPause: () => {
              stopDrawing();
              drawFrame();
            },
            onEnded: stopDrawing,
          })}
      >
        Your browser does not support the video tag.
      </video>

      <canvas
        ref={canvasRef}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
        }}
      />
    </div>
  );
};

export default EvaluationVideo;
